import React, { useEffect, useState, useRef } from "react";
import { marked } from "marked";
import { connect } from "react-redux";
import { toast } from "react-toastify";

import EventTypeForm from "../EventDetailsForm/EventTypeForm";
import { eventBookingActions } from "../../../../redux/actions";
import EventOnsiteForm from "../EventDetailsForm/EventOnsiteForm";
import EventSelection from "../../ShutterBSelection/EventSelection";
import EventTypeAIResponse from "../EventDetailsForm/EventTypeAIResponse";
import useResponsiveView from "../../../../shared/hooks/useResponsiveView";
import BackButton from "../../../../shared/components/IconButton/BackButton";
import {
  messages,
  EMPTY_STRING,
  eventDetailsFields,
  eventBookingStatus,
  eventBookingConstants,
} from "../../../../constants";
import {
  customToast,
  filterByStatus,
  removeDraftEvents,
  isEventDetailsValid,
  updateEventDetailAIResponse,
} from "../../../../shared";

const { TABS_ID } = eventBookingConstants;

const EventDetails = (props) => {
  const { currentEventType, onNext } = props;

  // Check if the viewport at specific width
  const { isGreaterThan } = useResponsiveView();

  const [isContactInfo, setIsContactInfo] = useState(false);
  const [currentContent, setCurrentContent] = useState(EMPTY_STRING);
  const [shouldScroll, setShouldScroll] = useState(false);
  const aiResponseRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line
  }, [isContactInfo]);

  useEffect(() => {
    if (props.activeTab === TABS_ID.EVENT_DETAIL) {
      window.scrollTo(0, 0);

      // Remove draft and Canceled events from the formState
      const updatedEvents = removeDraftEvents(
        filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL])
      );
      if (updatedEvents.length > 0) {
        const firstEvent = updatedEvents[0];
        props.setEventName(firstEvent.eventName);
        props.setSelectedEventId(firstEvent.id);
      } else {
        props.setSelectedEventId(null);
        props.setEventName(EMPTY_STRING);
      }
      setIsContactInfo(false);
    }
    // eslint-disable-next-line
  }, [props.activeTab]);

  // Update currentEventType when selectedEventId or formstate change
  useEffect(() => {
    const currentEvent = Array.isArray(props.formState)
      ? props.formState.find((event) => event.id === props.selectedEventId)
      : null;
    props.setCurrentEventType(currentEvent || null);
    setCurrentContent(
      currentEvent?.eventType?.description
        ? marked.parse(currentEvent.eventType.description)
        : EMPTY_STRING
    );
    // eslint-disable-next-line
  }, [props.selectedEventId, props.formState]);

  const toggleContactInfo = () => {
    if (!isEventDetailsValid(currentEventType.eventType)) {
      customToast(messages.EVENT_SHOT_LIST_IS_EMPTY, toast.TYPE.ERROR);
      return;
    }
    setIsContactInfo((prev) => !prev);
  };

  const handleEventSlider = (event) => {
    if (
      props.currentEventType?.id === props.selectedEventId &&
      props.currentEventType?.eventType?.description !== currentContent &&
      currentContent !== EMPTY_STRING &&
      !isContactInfo
    ) {
      const updatedFormState = updateEventDetailAIResponse({
        formState: props.formState,
        eventId: props.selectedEventId,
        eventType: eventDetailsFields.EVENT_TYPE,
        description: eventDetailsFields.DESCRIPTION,
        value: currentContent,
      });
      props.setFormState(updatedFormState);
    }

    props.setSelectedEventId(event.id);
    props.setEventName(event.eventName);
    setIsContactInfo(false);
  };

  const handleChange = (eventId, name, value) => {
    if (!eventId) return;

    const updatedFormState = props.formState.map((event) => {
      if (event.id !== eventId) return event;

      if (name === eventDetailsFields.EVENT_QUESTION_ANSWERS) {
        const updatedAnswers = [
          ...(event.eventQuestionAnswers || []).filter(
            (q) => q.questionTypeId !== value.questionTypeId
          ),
          value,
        ];

        return {
          ...event,
          eventQuestionAnswers: updatedAnswers,
        };
      }

      if (name === eventDetailsFields.EVENT_TYPE) {
        // Merge into the existing eventType object without removing other fields
        return {
          ...event,
          [eventDetailsFields.EVENT_TYPE]: {
            ...(event.eventType || {}),
            ...value,
          },
        };
      }

      return {
        ...event,
        [name]: value,
      };
    });
    props.setFormState(updatedFormState);
  };

  //Scroll to EventTypeAIResponse
  useEffect(() => {
    if (shouldScroll && aiResponseRef.current) {
      aiResponseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setShouldScroll(false);
    }
  }, [shouldScroll, aiResponseRef, props.currentEventType]);

  const scrollToAIResponse = () => {
    // Only scroll on tablet or mobile views
    if (isGreaterThan(992)) return;

    // If already rendered, scroll immediately; otherwise set flag for effect to pick up after render
    if (aiResponseRef.current) {
      aiResponseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      setShouldScroll(true);
    }
  };

  const handlePrevious = () => {
    props.setEventName(EMPTY_STRING);
    props.onPrevious();
  };

  return (
    <div className="event-details-container">
      <EventSelection
        formState={removeDraftEvents(
          filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL])
        )}
        handleEventClick={handleEventSlider}
        isDisabled
      />
      <div className="row">
        <div className="col-12">
          <hr className="mt-0" />
        </div>
      </div>

      <div className="row event-type-container">
        {!isContactInfo ? (
          <>
            <div className="col-lg-6 pr-md-0">
              <EventTypeForm
                onChange={handleChange}
                onGenerateShotlist={scrollToAIResponse}
              />
            </div>
            {currentEventType?.eventType?.description?.length > 0 && (
              <div className="col-lg-6 pl-md-0">
                <EventTypeAIResponse
                  ref={aiResponseRef}
                  onNext={toggleContactInfo}
                  currentContent={currentContent}
                  setCurrentContent={setCurrentContent}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="col-12">
              <EventOnsiteForm
                formState={removeDraftEvents(
                  filterByStatus(props.formState ?? [], [
                    eventBookingStatus.CANCEL,
                  ])
                )}
                onChange={handleChange}
                onPrevious={toggleContactInfo}
                onNext={onNext}
              />
            </div>
          </>
        )}
      </div>

      <BackButton
        className="event-back-button"
        onClick={handlePrevious}
        text={`${props.previousActiveTab}`}
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  formState: state.eventBooking.formState,
  selectedEventId: state.eventBooking.selectedEventId,
  currentEventType: state.eventBooking.currentEventType,
  previousActiveTab: state.eventBooking.previousActiveTab,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setEventName: (data) => dispatch(eventBookingActions.setEventName(data)),
  setSelectedEventId: (data) =>
    dispatch(eventBookingActions.setSelectedEventId(data)),
  setCurrentEventType: (data) =>
    dispatch(eventBookingActions.setCurrentEventType(data)),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(EventDetails);
